import type { AIModelChoices, Annotation } from 'api';
import { useTranslation } from 'i18n';
import { Alert, AlertTitle, Box, Divider, Stack, Switch, Typography, useBreakpoint } from 'ui';

import { getIgnoredLabels } from '../../constants';
import { SegmentationLegend } from '../SegmentationLegend/SegmentationLegend';

export const AiFilters = ({
  selectedAnnotation,
  hiddenCategories,
  handleHideCategory,
  handleToggleLegend,
  showLegend,
  shouldShowSidebarLegend = true,
  model,
  showAerosols = false,
  hasValidModelResults = true,
}: {
  selectedAnnotation: Annotation[];
  hiddenCategories: readonly string[];
  handleHideCategory: (label: string) => void;
  handleToggleLegend: (checked: boolean) => void;
  showLegend: boolean;
  shouldShowSidebarLegend?: boolean;
  model: AIModelChoices;
  showAerosols?: boolean;
  hasValidModelResults?: boolean;
}) => {
  const { isMobile } = useBreakpoint();
  const { t } = useTranslation('features');
  const ignoredLabels = getIgnoredLabels(model);
  const showSwitch = Boolean(selectedAnnotation?.some(({ label }) => !ignoredLabels.includes(label)));

  return (
    <Stack
      data-testid="ai-filters"
      sx={{
        gap: 2,
      }}
    >
      <Typography variant="h5" fontWeight="bold">
        {t('annotations.aiFilters.title')}
      </Typography>
      {!isMobile ? (
        <>
          <Typography variant="body1">{t('annotations.aiFilters.description')}</Typography>
          <Divider />
        </>
      ) : null}
      {showSwitch ? (
        <Switch
          checked={showLegend}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleToggleLegend(e.target.checked)}
          size="medium"
          data-testid="toggle-segmentation"
          label={t('annotations.aiFilters.showOutlines')}
        />
      ) : hasValidModelResults ? (
        <Alert severity="info" data-testid="no-filters-alert">
          <AlertTitle>
            <Typography fontWeight="bold">{t('annotations.aiFilters.noFiltersTitle')}</Typography>
          </AlertTitle>
          <Typography>{t('annotations.aiFilters.noFiltersDescription')}</Typography>
        </Alert>
      ) : (
        <Alert severity="warning" data-testid="no-model-results-alert">
          <AlertTitle>
            <Typography fontWeight="bold">{t('annotations.aiFilters.analysisFailedTitle')}</Typography>
          </AlertTitle>
          <Typography>{t('annotations.aiFilters.analysisFailedDescription')}</Typography>
        </Alert>
      )}
      {showLegend && shouldShowSidebarLegend ? (
        <Box
          sx={{
            paddingBottom: 1,
          }}
        >
          <SegmentationLegend
            annotations={selectedAnnotation ?? []}
            hiddenCategories={hiddenCategories}
            onSelectCategory={handleHideCategory}
            model={model}
            showAerosols={showAerosols}
          />
        </Box>
      ) : null}
    </Stack>
  );
};
